<template>
  <div class="py-4 container-fluid">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3">Bitte warten, Phonebot-Daten werden geladen...</p>
    </div>
    <!-- When not loading, always show content; display error banner only if error is set -->
    <div v-else>
      <div v-if="error" class="error-message mb-3">
        {{ error }}
        <button class="btn btn-sm btn-primary mt-2" @click="fetchVbots">
          Erneut versuchen
        </button>
      </div>

      <!-- Content -->
      <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom">
            <div class="user d-flex align-items-center">
              <div class="col-6">
                <h5 class="mb-0">Phonebots</h5>
              </div>
              <div class="col-6 text-end">
                <router-link to="/addvbot" v-if="role === 'admin' || role === 'adminuser'">
                  <material-button class="float-right btn btn-sm btn-success">
                    <i class="fas fa-user-plus me-2"></i> Add Phonebot
                  </material-button>
                </router-link>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table id="datatable-search" class="table table-flush">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Phonebot</th>
                  <th>License/UserKey</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!vbots || vbots.length === 0">
                  <td colspan="6" class="text-center">No Phonebots found.</td>
                </tr>
                <tr v-else v-for="(vbot, index) in vbots" :key="vbot.id || index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ vbot.bezeichnung || 'N/A' }}</td>
                  <td>{{ vbot.license_Key || 'N/A' }}</td>
                  <td>
                    <span class="badge" :class="getStatusClass(vbot.status)">
                      {{ vbot.status || 'N/A' }}
                    </span>
                  </td>
                  <td>{{ formatDate(vbot.created_at) }}</td>
                  <td>
                    <button class="btn btn-sm btn-info" @click="editItem(vbot.license_Key)" :title="'Edit Phonebot ' + vbot.bezeichnung">
                      <i class="fas fa-pen"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import userService from "@/services/users.service";
import Swal from 'sweetalert2';

export default {
  name: 'Vbots',
  data() {
    return {
      vbots: [],
      dataTable: null,
      initializationTimer: null,
      role: '',
      loading: true,
      error: null,
    };
  },

  async mounted() {
    await this.fetchVbots();
    await this.fetchRole();
  },

  beforeUnmount() {
    this.cleanupDataTable();
  },

  watch: {
    // Watch vbots changes für sichere DataTable updates
    vbots: {
      handler() {
        if (this.vbots && this.vbots.length > 0) {
          this.safeInitializeDataTable();
        }
      },
      deep: true
    }
  },

  methods: {
    getStatusClass(status) {
      switch (status) {
        case 'active':
          return 'badge-success';
        case 'inactive':
          return 'badge-secondary';
        default:
          return 'badge-warning';
      }
    },

    async fetchVbots() {
      try {
        this.loading = true;
        this.error = null;
        const response = await this.$store.dispatch('vbot/getVbots');

        if (Array.isArray(response)) {
          this.vbots = response;
        } else {
          console.error('❌ Response is not an array:', response);
          this.vbots = [];
        }
        
      } catch (error) {
        console.error('❌ Error fetching phonebots:', error);
        // If backend returns 404 (no vbots), treat as empty list instead of showing an error
        if (error && error.response && error.response.status === 404) {
          this.vbots = [];
          this.error = null; // don't show an error banner for 'not found'
        } else {
          this.error = "Fehler beim Laden der Phonebot-Daten. Bitte versuchen Sie es erneut.";
          this.vbots = [];
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchRole() {
      try {
        const response = await userService.fetchRole();
        this.role = response.role;
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }

      try {
        const formattedDate = date.toISOString().split('T')[0];
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${formattedDate}, ${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    },

    editItem(license_Key) {
      this.$router.push({ name: "EditVbot", params: { license_Key: license_Key } });
    },

    viewCallLogs(vid) {
      this.$router.push({ name: 'ViewCallLogs', params: { id: vid } });
    },

    async deleteItem(vid) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('vbot/deleteVbot', vid);
          this.vbots = this.vbots.filter(vbot => vbot.id !== vid);
          this.refreshDataTable();
          Swal.fire('Deleted!', 'Phonebot has been deleted.', 'success');
        } catch (error) {
          console.error(`Error deleting phonebot with ID: ${vid}`, error);
          Swal.fire('Error!', 'There was an error deleting the phonebot. Please try again.', 'error');
        }
      }
    },

    // ROBUSTE DataTable Initialisierung (übernommen von Phonebots.vue)
    safeInitializeDataTable() {
      // Clear existing timer
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
      }

      // Warte auf DOM updates und dann initialisiere
      this.initializationTimer = setTimeout(() => {
        this.$nextTick(() => {
          this.initializeDataTable();
        });
      }, 100); // 100ms delay für sicherere DOM-State
    },

    // SAUBERE DataTable Bereinigung (übernommen von Phonebots.vue)
    cleanupDataTable() {
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
        this.initializationTimer = null;
      }

      if (this.dataTable) {
        try {
          // Event handlers entfernen
          $('#datatable-search tbody').off('click', '.edit-btn');
          
          // DataTable sicher zerstören
          if ($.fn.DataTable.isDataTable('#datatable-search')) {
            this.dataTable.clear();
            this.dataTable.destroy(true); // true = remove from DOM completely
          }
          
          this.dataTable = null;
        } catch (error) {
          console.warn('⚠️ Error cleaning up DataTable:', error);
          this.dataTable = null;
        }
      }
    },

    initializeDataTable() {
      // Prüfe ob DOM-Element verfügbar ist
      const tableElement = document.getElementById('datatable-search');
      if (!tableElement) {
        setTimeout(() => this.safeInitializeDataTable(), 50);
        return;
      }

      // Prüfe ob Daten verfügbar sind
      if (!this.vbots || this.vbots.length === 0) {
        return;
      }

      try {
        
        // Komplett saubere Bereinigung vor Initialisierung
        this.cleanupDataTable();

        // Sicherheitscheck: Warte noch einen Tick
        this.$nextTick(() => {
          try {
            this.dataTable = $('#datatable-search').DataTable({
              destroy: true, // Automatische Bereinigung falls bereits existiert
              data: this.vbots,
              autoWidth: false, // Verhindere Layout-Probleme
              columnDefs: [
                { width: "5%", targets: 0, orderable: false },      // #
                { width: "25%", targets: 1 },                       // Phonebot
                { width: "35%", targets: 2, className: "license-column" }, // License Key
                { width: "15%", targets: 3 },                       // Status
                { width: "15%", targets: 4 },                       // Created At
                { width: "5%", targets: 5, orderable: false }       // Actions
              ],
              columns: [
                { 
                  data: null,
                  title: '#',
                  render: (data, type, row, meta) => meta.row + 1,
                  width: '50px',
                  orderable: false
                },
                { 
                  data: 'bezeichnung',
                  title: 'Phonebot',
                  defaultContent: 'N/A'
                },
                { 
                  data: 'license_Key',
                  title: 'License/UserKey',
                  defaultContent: 'N/A'
                },
                { 
                  data: 'status',
                  title: 'Status',
                  render: (data) => {
                    const statusClass = this.getStatusClass(data);
                    return `<span class="badge ${statusClass}">${data || 'N/A'}</span>`;
                  },
                  defaultContent: '<span class="badge badge-warning">N/A</span>'
                },
                {
                  data: 'created_at',
                  title: 'Created At',
                  render: (data) => this.formatDate(data),
                  defaultContent: '-'
                },
                {
                  data: null,
                  title: 'Actions',
                  render: (data) => {
                    const licenseKey = data.license_Key || data.id;
                    
                    if (!licenseKey) {
                      console.error('❌ Kein license_Key für Phonebot:', data.bezeichnung);
                      return '<span class="text-danger">Kein Key</span>';
                    }
                    
                    return `
                      <button class='btn btn-sm btn-info edit-btn' data-id='${licenseKey}' title='Edit ${data.bezeichnung || 'Phonebot'}'>
                        <i class='fas fa-pen'></i>
                      </button>
                    `;
                  },
                  orderable: false,
                  width: '80px'
                },
              ],
              order: [[0, 'asc']],
              pageLength: 25,
              responsive: true,
              language: {
                emptyTable: "Keine Phonebots verfügbar",
                info: "Zeige _START_ bis _END_ von _TOTAL_ Einträgen",
                infoEmpty: "Keine Einträge verfügbar", 
                infoFiltered: "(gefiltert von _MAX_ Gesamteinträgen)",
                lengthMenu: "Zeige _MENU_ Einträge",
                search: "Suchen:",
                zeroRecords: "Keine passenden Einträge gefunden",
                paginate: {
                  first: "‹‹",
                  last: "››",
                  next: "›",
                  previous: "‹"
                }
              },
              drawCallback: () => {
                // Sichere Border-Anwendung
                this.$nextTick(() => {
                  this.applyCustomBorders();
                });
              }
            });

            // Event handlers sicher hinzufügen
            this.$nextTick(() => {
              $('#datatable-search tbody').off('click', '.edit-btn'); // Remove old handlers first
              $('#datatable-search tbody').on('click', '.edit-btn', (event) => {
                const id = $(event.currentTarget).data('id');
                this.editItem(id);
              });
            });

            this.applyCustomBorders();
            
          } catch (error) {
            console.error('❌ Error initializing DataTable:', error);
          }
        });

      } catch (error) {
        console.error('❌ Error in DataTable initialization:', error);
      }
    },

    refreshDataTable() {
      if (this.dataTable) {
        this.dataTable.clear().rows.add(this.vbots).draw();
      }
    },

    applyCustomBorders() {
      try {
        $('#datatable-search th, #datatable-search td').css({
          'border': '1px solid #ddd',
          'padding': '10px',
        });

        $('#datatable-search td:first-child, #datatable-search th:first-child').css({
          'border-left': '2px solid #ddd',
        });

        $('#datatable-search th').css({
          'border-top': '2px solid #ddd',
        });
      } catch (error) {
        console.error('❌ Error applying borders:', error);
      }
    },
  },
};
</script>

<style scoped>
#datatable-search {
  table-layout: fixed;
  width: 100%;
}

#datatable-search td {
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Spezielle Behandlung für License Key Spalte */
.license-column {
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
}

#datatable-search td button {
  margin-right: 5px;
}

/* Loading State Styles */
.loading-container {
  padding: 80px 20px;
  text-align: center;
  color: #666;
}

.error-message {
  padding: 40px 20px;
  text-align: center;
  background-color: #fee;
  color: #c00;
  border-radius: 8px;
  margin: 20px;
}

.badge-success {
  background-color: #28a745;
}

.badge-secondary {
  background-color: #6c757d;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}
</style>